import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import LandingPage from "@/pages/LandingPage";
import MainApp from "@/pages/MainApp";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const axiosInstance = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Add interceptor to include token from localStorage as fallback for mobile
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token && !config.headers['Authorization']) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Check for session_id in URL hash BEFORE React renders
const getSessionIdFromHash = () => {
  const hash = window.location.hash;
  const match = hash.match(/session_id=([^&]+)/);
  if (match) {
    const sessionId = match[1];
    // Clear hash immediately
    window.history.replaceState(null, "", window.location.pathname);
    return sessionId;
  }
  return null;
};

// Get session ID synchronously before component mounts
const initialSessionId = getSessionIdFromHash();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingAuth, setProcessingAuth] = useState(!!initialSessionId);
  const authProcessedRef = useRef(false);

  useEffect(() => {
    // Prevent double processing (React StrictMode calls useEffect twice)
    if (authProcessedRef.current) return;
    authProcessedRef.current = true;
    
    if (initialSessionId) {
      processSessionId(initialSessionId);
    } else {
      // Also check hash again in case it wasn't caught initially (mobile Safari edge case)
      const hash = window.location.hash;
      const match = hash.match(/session_id=([^&]+)/);
      if (match) {
        window.history.replaceState(null, "", window.location.pathname);
        setProcessingAuth(true);
        processSessionId(match[1]);
      } else {
        checkExistingSession();
      }
    }
  }, []);

  const processSessionId = async (sessionId) => {
    try {
      const response = await axiosInstance.post("/auth/session", { session_id: sessionId });
      // Store token in localStorage as fallback for mobile browsers
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      setUser(response.data.user);
    } catch (error) {
      console.error("Auth error:", error);
      // On auth error, try checking existing session (cookie might already be set)
      try {
        const meResponse = await axiosInstance.get("/auth/me");
        setUser(meResponse.data);
      } catch (meError) {
        console.log("No existing session found");
      }
    } finally {
      setProcessingAuth(false);
      setLoading(false);
    }
  };

  const checkExistingSession = async () => {
    try {
      const response = await axiosInstance.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      console.log("No active session");
      // Clear any stale token
      localStorage.removeItem('session_token');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axiosInstance.post("/auth/logout");
      localStorage.removeItem('session_token');
      setUser(null);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  if (loading || processingAuth) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-teal-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={user ? <Navigate to="/app" /> : <LandingPage />} />
          <Route path="/app/*" element={user ? <MainApp user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;